import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { from, Observable, of } from 'rxjs';
import { FormGroup } from '@angular/forms';

import { CustomerApiFacade } from './customer-api.facade';
import { CustomerFormService } from './customer-form.service';
import { CombinedCustomer, CustomerCreation, CustomerDetails, CustomerWithContact } from './customer.model';
import { CustomerActions } from './store/action-types';
import { CustomerFeatureState, customerSelectors } from './store';
import { SnackbarService } from 'services/snackbar.service';
import { catchError, concatMap, defaultIfEmpty, first, map, switchMap, tap, toArray } from 'rxjs/operators';
import { Country } from '@domains/viewing-pass';
import { PagingOptions } from 'src/app/common-models/paging-options';
import { CustomerLocalDbService } from "./customer-local-db.service";
import { LOCAL_DOCUMENT_ID_PREFIX } from 'src/app/offline/local-database';

@Injectable({ providedIn: 'root' })
export class CustomerDataFacade {
  customersListData$ = this._store$.select(customerSelectors.customersListData);
  customersListMetadata$ = this._store$.select(customerSelectors.customersListMetadata);
  customersLoading$ = this._store$.select(customerSelectors.customersLoading);
  customerDetails$ = this._store$.select(customerSelectors.customerDetails);
  customerDetailsLoading$ = this._store$.select(customerSelectors.customerDetailsLoading);
  customerDetailsLoadingError$ = this._store$.select(customerSelectors.customerDetailsLoadingError);
  customerDuplicationCheck$ = this._store$.select(customerSelectors.customerDuplicationCheck);
  getAllCustomers$ = this._store$.select(customerSelectors.allCustomersListData);
  allCustomersLoading$ = this._store$.select(customerSelectors.allCustomersLoading);
  allCustomersListMetadata$ = this._store$.select(customerSelectors.allCustomersListMetadata);

  constructor(
    private readonly _store$: Store<CustomerFeatureState>,
    private readonly _snackbar: SnackbarService,
    private readonly _customerApiFacade: CustomerApiFacade,
    private readonly _customerFormService: CustomerFormService,
    private readonly _customerOfflineService: CustomerLocalDbService,
  ) { }

  getRelatedPropertyCustomers$(propertyId: string): Observable<CombinedCustomer[]> {
    return this._customerApiFacade.getRelatedPropertyCustomers$(propertyId);
  }

  loadAllCustomers(pagingOptions: PagingOptions, searchText?: string): void {
    this._store$.dispatch(CustomerActions.LoadAllCustomers({ pagingOptions, searchText }));
  }

  loadCustomers(pagingOptions: PagingOptions, searchText?: string): void {
    this._store$.dispatch(CustomerActions.LoadCustomers({ pagingOptions, searchText }));
  }

  loadCustomerDetails(id: string): void {
    this._store$.dispatch(CustomerActions.LoadCustomerDetails({ payload: { id } }));
  }

  saveRentalContractCustomers(
    contractTenants: CustomerWithContact[],
    contractLandlords: CustomerWithContact[],
    rentalContractId: string,
    objectId: string,
  ): void {
    this._store$.dispatch(CustomerActions.SaveRentalContractCustomers({
      contractTenants,
      contractLandlords,
      rentalContractId,
      objectId,
    }));
  }

  triggerExternalCustomersLoadingSucceeded(): void {
    this._store$.dispatch(CustomerActions.TriggerExternalCustomersLoadingSucceeded());
  }

  deleteCustomer$(customerId: string): void {
    return this._store$.dispatch(CustomerActions.DeleteCustomer({ customerId }))
  }

  searchCustomer$(firstName: string, lastName: string): Observable<CustomerWithContact[]> {
    return this._customerApiFacade.searchCustomer$(firstName, lastName);
  }

  getCustomerById$(id: string): Observable<CustomerWithContact> {
    return this._customerApiFacade.getCustomerById$(id);
  }

  getCountries$(): Observable<Country[]> {
    return this._customerApiFacade.getCountries$();
  }

  getExternalCustomerById$(id: string): Observable<CustomerDetails> {
    return this._customerApiFacade.getExternalCustomerById$(id);
  }

  importEdirealCustomer$(data: CustomerCreation): Observable<boolean> {
    return this._customerApiFacade.importEdirealCustomer$(data).pipe(
      tap((response) => {
        if (response) {
          this._snackbar.showSnackbar(
            'Kunde erfolgreich importiert',
            'mat-primary',
            true
          );
        } else {
          this._snackbar.showSnackbar(
            'Kunde konnte nicht importiert werden',
            'mat-warn',
            true
          );
        }
      })
    )
  }

  saveCustomer$(customerForm: FormGroup): Observable<CustomerWithContact | null> {
    return this._prepareAndValidateCustomer(customerForm, customerData =>
      this._customerApiFacade.createCustomer$(customerData)
    );
  }

  saveCustomersOnline$(customersForm: FormGroup[]): Observable<string[]> {
    return from(customersForm).pipe(
      concatMap((customerForm: FormGroup) =>
        this.saveCustomer$(customerForm).pipe(
          first(),
          defaultIfEmpty(null as any)
        )
      ),
      toArray(),
      map((data) => data
        .filter(Boolean)
        .map((customer: any) => customer?.id as string)
      ),
    );
  }

  downloadCustomerData$(): Observable<any> {
    return this._customerApiFacade.downloadCustomerData$();
  }

  saveCustomerOffline$(customerForm: FormGroup): Observable<CustomerWithContact | null> {
    return this._prepareAndValidateCustomer(customerForm, customerData =>
      this._saveCustomerOffline$(customerData)
    );
  }

  saveCustomersOffline$(customersForm: FormGroup[]): Observable<string[]> {
    return from(customersForm).pipe(
      concatMap((customerForm: FormGroup) =>
        this.saveCustomerOffline$(customerForm).pipe(
          first(),
          defaultIfEmpty(null as any)
        )
      ),
      toArray(),
      map((data) => data
        .filter(Boolean)
        .map((customer: any) => customer?.id as string)
      ),
    );
  }

  syncLocalCustomer$(id: string) {
    return this._customerOfflineService.get$(id).pipe(
      defaultIfEmpty(null),
      switchMap((customerData: any) => {
        if (!customerData) {
          return of(null);
        }

        if (customerData?.id.startsWith(LOCAL_DOCUMENT_ID_PREFIX)) {
          customerData.id = '';
        }

        return this._customerApiFacade.createCustomer$(customerData).pipe(
          switchMap((customer) =>
            this._customerOfflineService.delete$(id).pipe(map(() => customer.id))
          ),
          catchError((error) => {
            console.error('Failed to sync local customer:', error);
            return of(null);
          })
        );
      }),
      catchError((error) => {
        console.error('Failed to retrieve customer:', error);
        return of(null);
      }),
    );
  }

  syncLocalCustomers$(ids: string[]): Observable<string[]> {
    return from(ids).pipe(
      concatMap((id) =>
        this.syncLocalCustomer$(id).pipe(
          catchError((error) => {
            console.error(`Failed to sync customer with ID ${id}:`, error);
            return of(null);
          })
        )
      ),
      toArray(),
      map((data) => data.filter(Boolean) as string[])
    );
  }

  private _prepareAndValidateCustomer(
    customerForm: FormGroup,
    saveMethod: (data: any) => Observable<CustomerWithContact | null>
  ): Observable<CustomerWithContact | null> {
    if (!this._validateCustomerForm(customerForm)) {
      return of(null);
    }

    const customerData = this._customerFormService.prepareCustomerToPersist(customerForm.value);
    const mappedCustomerData = this._mapCustomerData(customerData);

    if (!this._isValidCustomer(mappedCustomerData as any)) {
      console.warn('Invalid customer data', mappedCustomerData);
      return of(null);
    }

    return saveMethod(mappedCustomerData);
  }

  private _saveCustomerOffline$(customerData: any) {
    return this._customerOfflineService.save$(customerData).pipe(
      tap(() => console.log('Customer saved offline')),
      catchError((offlineError) => {
        console.error('Failed to save customer offline:', offlineError);
        return of(null);
      })
    );
  }

  private _mapCustomerData(customer: any): CustomerCreation {
    const IDImages = (customer.IDImages ?? []).map(
      ({ id, storageUrl, type, description }: any) => ({ id, storageUrl, type, description })
    );

    return {
      ...customer,
      IDImages,
    };
  }

  private _isValidCustomer(customer: CustomerWithContact): boolean {
    const firstnameValid = !!customer.firstname;
    const lastnameValid = !!customer.lastname;
    const emailValid = !!customer.contact.email;

    return firstnameValid && lastnameValid && emailValid;
  }

  private _validateCustomerForm(form: FormGroup): boolean {
    form.updateValueAndValidity();
    form.markAllAsTouched();

    if (form.invalid) {
      console.warn('Customer Form Invalid!', form.value);
      return false;
    }

    return true;
  }

  public startCustomersDuplicationCheck(): void {
    this._store$.dispatch(CustomerActions.StartCustomersDuplicationCheck());
  }
  public resetCustomersDuplicationCheck(): void {
    this._store$.dispatch(CustomerActions.ResetCustomersDuplicationCheck());
  }
}
